import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import clsx from 'clsx';

import { ReactComponent as Logo } from '../../../assets/icons/brand/logo_32.svg';
import { ReactComponent as BugReportsIcon } from '../../../assets/icons/menu/bug_reports.svg';
import { ReactComponent as CommunitiesIcon } from '../../../assets/icons/menu/communities.svg';
import { ReactComponent as ContactsIcon } from '../../../assets/icons/menu/contacts.svg';
import { ReactComponent as ExerciseIcon } from '../../../assets/icons/menu/exercise.svg';
import { ReactComponent as LogoutIcon } from '../../../assets/icons/menu/logout.svg';
import { ReactComponent as MyProfileIcon } from '../../../assets/icons/menu/my_profile.svg';
import { ReactComponent as NewsIcon } from '../../../assets/icons/menu/news.svg';
import { ReactComponent as ProgramIcon } from '../../../assets/icons/menu/program.svg';
import { ReactComponent as ResourcesIcon } from '../../../assets/icons/menu/resources.svg';
import { ReactComponent as SettingsIcon } from '../../../assets/icons/menu/settings.svg';
import { ReactComponent as StatisticIcon } from '../../../assets/icons/menu/statistic.svg';
import { ReactComponent as SubscriptionsIcon } from '../../../assets/icons/menu/subscriptions.svg';
import { ReactComponent as TrainingIcon } from '../../../assets/icons/menu/training.svg';
import { ReactComponent as UsersIcon } from '../../../assets/icons/menu/users.svg';
import { Paths } from '../../../shared/paths';
import CloseActionButton from '../../designe/ActionButtons/CloseActionButton';
import ThemeSwitch from '../../designe/app-theme-switch/ThemeSwitch';
import { IMenuProps, useMenu } from './useMenu';

interface IMenuItem {
  icon: React.ReactNode;
  label: string;
  link: string;
  activeKey: string;
  divider?: boolean;
  onboardingKey?: string;
}

const Menu = (props: IMenuProps) => {
  const {
    t,
    theme,
    classes,
    planOrDonationsMenuVisible,
    subscriptionFeatureActive,
    donationsFeatureActive,
    resoursesFeatureActive,
    policyMenuLinks,
    displayedVersion,
    closeMenu,
    isActiveTab,
    onLinkClick,
    handleLogout,
    openPolicyModal,
  } = useMenu(props);

  const menuItems: IMenuItem[] = [
    {
      icon: <TrainingIcon />,
      label: t('menu.trainings'),
      link: Paths.trainings.toMy,
      activeKey: 'trainings',
      onboardingKey: 'general.menu.trainings',
    },
    {
      icon: <ProgramIcon />,
      label: t('menu.programs'),
      link: Paths.programs.toItem,
      activeKey: 'programs',
      onboardingKey: 'general.menu.programs',
    },
    {
      icon: <ExerciseIcon />,
      label: t('common.exercises'),
      link: Paths.exercises.toPublic,
      activeKey: 'exercises',
      onboardingKey: 'general.menu.exercises',
    },
    {
      icon: <StatisticIcon />,
      label: t('menu.statistics'),
      link: Paths.statistics.toCalendar,
      activeKey: 'statistics',
      divider: true,
      onboardingKey: 'general.menu.statistics',
    },
    {
      icon: <MyProfileIcon />,
      label: t('common.myProfile'),
      link: Paths.users.me,
      activeKey: 'me',
    },
    {
      icon: <UsersIcon />,
      label: t('common.users'),
      link: Paths.users.toList,
      activeKey: 'users',
    },
    {
      icon: <CommunitiesIcon />,
      label: t('common.communities'),
      link: Paths.communities.toList,
      activeKey: 'communities',
    },
    {
      icon: <NewsIcon />,
      label: t('common.news'),
      link: Paths.news.root,
      activeKey: 'news',
      divider: true,
    },
    planOrDonationsMenuVisible
      ? {
          icon: <SubscriptionsIcon />,
          label:
            !subscriptionFeatureActive && donationsFeatureActive
              ? t('menu.projectSupport')
              : t('menu.subscriptionPlan'),
          link: Paths.plan.root,
          activeKey: 'plan',
        }
      : null,

    {
      icon: <SettingsIcon />,
      label: t('common.settings'),
      link: Paths.settings.root,
      activeKey: 'settings',
    },
    {
      icon: <ContactsIcon />,
      label: t('menu.contacts'),
      link: Paths.contacts,
      activeKey: 'contacts',
    },
    resoursesFeatureActive
      ? {
          icon: <ResourcesIcon />,
          label: t('menu.resources'),
          link: Paths.resources,
          activeKey: 'resources',
        }
      : null,
    {
      icon: <BugReportsIcon />,
      label: t('menu.bugReports'),
      link: Paths.bugReports.root,
      activeKey: 'bug-reports',
      divider: true,
    },
  ].filter(Boolean) as IMenuItem[];

  const mobileItems: IMenuItem[] = [];

  return (
    <nav className={classes.menuWrapper} onClick={closeMenu}>
      <div className={classes.menuContainer}>
        <Box className={classes.menuHeader}>
          <Box className={classes.logoContainer}>
            <Logo />
            <Typography variant="h2">{t('common.companyName')}</Typography>
          </Box>
          <CloseActionButton
            onClick={closeMenu}
            styles={{
              color: theme.palette.menu.contrastText,
            }}
          />
        </Box>
        <div className={classes.menuList}>
          {menuItems?.map((item) => (
            <React.Fragment key={item.label}>
              <Link
                to={item.link}
                onClick={(e) => onLinkClick(e, item.link)}
                className={classes.menuItemLink}>
                <li
                  data-onboarding={item.onboardingKey}
                  className={clsx(
                    classes.menuItem,
                    classes.menuItemClickable,
                    isActiveTab(item) && classes.active,
                  )}>
                  {item.icon}
                  <Typography variant="text-md">{item.label}</Typography>
                  {/*{!!item.counter && (*/}
                  {/*  <span className={classes.counter}>*/}
                  {/*    {item.counter < 100 ? item.counter : '99+'}*/}
                  {/*  </span>*/}
                  {/*)}*/}
                  {/*<ArrowForwardIosIcon />*/}
                </li>
              </Link>
              {item.divider && <Box className={classes.divider} />}
            </React.Fragment>
          ))}
          {mobileItems?.map((item) => (
            <React.Fragment key={item.label}>
              <Box
                className={clsx(classes.divider, classes.mobileMenuItemLink)}
              />
              <Link
                to={item.link}
                className={clsx(
                  classes.menuItemLink,
                  classes.mobileMenuItemLink,
                )}>
                <li
                  className={clsx(
                    classes.menuItem,
                    classes.menuItemClickable,
                    isActiveTab(item) && classes.active,
                  )}>
                  {item.icon}
                  <Typography variant="text-md">{item.label}</Typography>
                </li>
              </Link>
            </React.Fragment>
          ))}
          <Box
            component="div"
            className={classes.themeMenuRow}
            onClick={(e) => e.stopPropagation()}>
            <Box
              aria-hidden
              sx={{
                width: 24,
                flexShrink: 0,
              }}
            />
            <Typography variant="text-md" sx={{ flex: 1 }}>
              {t('menu.theme')}
            </Typography>
            <ThemeSwitch />
          </Box>
          <Box
            onClick={() => handleLogout()}
            className={classes.menuItemLink}
            mb={1}>
            <Box
              component="div"
              className={clsx(
                classes.menuItem,
                classes.menuItemClickable,
                classes.logoutMenuItem,
              )}>
              <LogoutIcon />
              <Typography variant="text-md">{t('common.logOut')}</Typography>
            </Box>
          </Box>
          <Box className={classes.divider} />
          <Box
            className={classes.bottomInfoSection}
            onClick={(e) => e.stopPropagation()}>
            <Box className={classes.policyLinksSection}>
              <Box className={classes.policyLinksWrap}>
                {policyMenuLinks.map(({ agreementType, i18nKey }) => (
                  <button
                    key={agreementType}
                    type="button"
                    className={classes.policyLinkButton}
                    onClick={() => openPolicyModal(agreementType)}>
                    <Typography variant="text-sm" component="span">
                      {t(i18nKey)}
                    </Typography>
                  </button>
                ))}
              </Box>
            </Box>
            <Typography variant="text-sm" className={classes.versionText}>
              {`${t('menu.version')}: ${displayedVersion || '-'}`}
            </Typography>
          </Box>
        </div>
      </div>
    </nav>
  );
};

export default Menu;
